import { Component, type FormEvent, type SyntheticEvent } from "react";
import { clone, equals } from "ramda";
import { observer } from "mobx-react";
import {
    Stack,
    DefaultButton,
    TextField,
    Toggle,
    DatePicker,
    Separator,
    Dropdown,
    type IDropdownOption,
    TooltipHost,
} from "@fluentui/react";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import SearchableDropdown from "../atoms/searchable-dropdown";
import {
    type Department,
    EngineType,
    ManagementRole,
    ProcurementType,
    type ShockProfile,
    type User,
    type Vehicle,
    type VehicleDevice,
    VehicleDeviceStatus,
    VehicleStatus,
    type VehicleType,
    type VehicleUpdate,
} from "../../api";
import { createUuid, type UUID } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import {
    doubleBindIntegerToString,
    doubleBindNullableString,
    doubleBindNullableStringEmptyAsNull,
    doubleBindString,
} from "../../utils/double-bind";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ModalConfirmation } from "../atoms/modal-confirmation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { mapSome } from "../../utils/functions";
import { defaultPageSize } from "../../utils/constants";
import { ClearableDropdown } from "../atoms/clearable-dropdown";

export interface FormUpdateVehicleProps extends FormUpdateProps<Vehicle> {}

import { store } from "../../store";

@observer
export class FormUpdateVehicle extends Component<FormUpdateVehicleProps> {
    @observable private accessor cancelConfirmationModalVisible = false;
    @observable public accessor lastVehicleDeviceId: UUID | null | undefined;
    @observable public accessor newVehicleDeviceId: UUID | null | undefined;
    @observable private accessor lastDriver: User | undefined;

    private validationId = createUuid();

    constructor(props: FormUpdateVehicleProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositoryVehicles.validation.update.updateModel(
            this.validationId,
            this.vehicleUpdate,
        );
    }

    componentDidMount(): void {
        // Don't initialize if we don't have props yet.
        // This will otherwise fail.
        if (!this.props) {
            return;
        }
        store.repositoryVehicles.validation.update.initializeModel(
            this.validationId,
            this.vehicleUpdate,
            this.props.id,
        );

        store.repositoryUsers
            .getLastDriverByVehicle(this.vehicle!.id)
            .then((user) => runInAction(() => (this.lastDriver = user)));

        this.getVehicleDeviceId();
    }

    @action.bound private getVehicleDeviceId(): void {
        const vehicleDevice = store.repositoryVehicleDevices.byVehicleId(this.props.id);
        this.lastVehicleDeviceId = vehicleDevice?.id;
        this.newVehicleDeviceId = vehicleDevice?.id;
    }

    public componentWillUnmount(): void {
        store.repositoryVehicles.discardMutableCopy(FormUpdateVehicle.name, this.props.id);
    }

    @computed private get vehicle(): Vehicle | undefined {
        return store.repositoryVehicles.mutableCopyById(FormUpdateVehicle.name, this.props.id);
    }

    @computed private get vehicleUpdate(): VehicleUpdate {
        const vehicle = clone(this.vehicle!);
        const update: VehicleUpdate = vehicle;
        update.active = vehicle.status != VehicleStatus.INACTIVE;
        return update;
    }

    @action.bound
    private async updateVehicle(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const vehicle = await store.repositoryVehicles.update(this.props.id, this.vehicleUpdate);
        if (this.lastVehicleDeviceId != this.newVehicleDeviceId) {
            if (this.lastVehicleDeviceId != null)
                await store.repositoryVehicleDevices.update(this.lastVehicleDeviceId, {
                    vehicleId: null,
                });
            if (this.newVehicleDeviceId != null)
                await store.repositoryVehicleDevices.update(this.newVehicleDeviceId, {
                    vehicleId: vehicle.id,
                });
            await store.repositoryVehicleDevices.reloadQuery({
                pageSize: defaultPageSize,
                vehicleId: vehicle.id,
            });
            await store.repositoryVehicleDevices.reloadQuery({
                pageSize: defaultPageSize,
                status: VehicleDeviceStatus.NOT_ASSIGNED,
            });
        }

        this.props.onUpdate(vehicle);
    }

    @action.bound private setActive(_event: unknown, flag: boolean | undefined): void {
        if (!this.vehicle) {
            throw new Error("Can't update active: Vehicle not loaded.");
        }
        if (flag) {
            this.vehicle.status = VehicleStatus.ACTIVE;
        } else {
            this.vehicle.status = VehicleStatus.INACTIVE;
        }
        this.triggerValidation();
    }

    @action.bound private setShockProfile(shockProfileId?: UUID): void {
        if (!this.vehicle) {
            throw new Error("Can't update shock profile id: Vehicle not loaded.");
        }
        this.vehicle.shockProfileId = shockProfileId ?? null;
        this.triggerValidation();
    }

    @action.bound private setVehicleDevice(vehicleDeviceId?: UUID): void {
        this.newVehicleDeviceId = vehicleDeviceId ?? null;
        this.triggerValidation();
    }

    @action.bound private setVehicleType(vehicleTypeId?: UUID): void {
        if (this.vehicle === undefined) {
            throw new Error("Can't update vehicle type: Vehicle not loaded.");
        }
        this.vehicle.vehicleTypeId = vehicleTypeId ?? null;
        this.triggerValidation();
    }

    @action.bound private setDepartment(departmentId?: UUID): void {
        if (this.vehicle === undefined) {
            throw new Error("Can't update department: Vehicle not loaded.");
        }
        this.vehicle.departmentId = departmentId ?? null;
        this.triggerValidation();
    }

    @action.bound private setChecklistOverride(_event: unknown, flag: boolean | undefined): void {
        if (!this.vehicle) {
            throw new Error("Can't update checklistOverride: Vehicle not loaded.");
        }
        this.vehicle.checklistOverride = flag ?? false;
        this.triggerValidation();
    }

    @action.bound private setMaintenanceLockdown(_event: unknown, flag: boolean | undefined): void {
        if (!this.vehicle) {
            throw new Error("Can't update maintenanceLockdown: Vehicle not loaded.");
        }
        this.vehicle.maintenanceLockdown = flag ?? false;
        this.triggerValidation();
    }

    @action.bound private setManufactureYear(
        _event: FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
        _index?: number,
        _value?: string,
    ): void {
        option == undefined
            ? (this.vehicle!.manufactureYear = null)
            : (this.vehicle!.manufactureYear = option?.key as number);
        this.triggerValidation();
    }

    @action.bound private setPreOpsChecklist(preOpsChecklistId?: UUID): void {
        if (this.vehicle === undefined) {
            throw new Error("Can't update pre ops checklist: Vehicle not loaded.");
        }
        this.vehicle.preOpsChecklistId = preOpsChecklistId ?? null;
        this.triggerValidation();
    }
    /**
     * This is run, if the user clicks the form's cancel button.
     * A confirmation modal might pop up, if the user did any changes.
     */
    @action.bound private showConfirmAndCallDialogCancelCallback(): void {
        // If there were any changes, i.e. if the original and mutable copy aren't the same,
        // open the confirmation modal for aborting the update process.
        const original = store.repositoryVehicles.byId(this.props.id);
        if (!equals(original, this.vehicle)) {
            this.cancelConfirmationModalVisible = true;
            return;
        }

        this.props.onDialogCancel();
    }

    @action.bound private setPhoneImei(
        _event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined,
    ): void {
        if (!this.vehicle) {
            throw new Error("Can't update phoneImei: Vehicle not loaded.");
        }
        this.vehicle.phoneImei = newValue == "" ? null : newValue;
        this.triggerValidation();
    }

    @action.bound private setIdleAutoLogoutAfter(
        _event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined,
    ): void {
        if (newValue == undefined) return;
        if (!this.vehicle) {
            throw new Error("Can't update idleAutoLogoutAfter: Vehicle not loaded.");
        }
        this.vehicle.idleAutoLogoutAfter = newValue == "" ? null : parseInt(newValue);
        this.triggerValidation();
    }

    @action.bound private setMaintenanceNotificationAt(
        _event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string | undefined,
    ): void {
        if (newValue == undefined) return;
        if (!this.vehicle) {
            throw new Error("Can't update maintenanceNotificationAt: Vehicle not loaded.");
        }
        this.vehicle.maintenanceNotificationAt = newValue == "" ? null : parseInt(newValue);
        this.triggerValidation();
    }

    @action.bound private setProcurementType(key?: string): void {
        if (!this.vehicle) {
            throw new Error("Can't update procurementType: Vehicle not loaded.");
        }
        this.vehicle.procurementType =
            ProcurementType[String(key).toUpperCase() as keyof typeof ProcurementType] || null;
        this.triggerValidation();
    }

    @action.bound private setPurchaseDate(date: Date | null | undefined): void {
        if (!this.vehicle) {
            throw new Error("Can't update purchaseDate: Vehicle not loaded.");
        }
        this.vehicle.purchaseDate = date ?? undefined;
        this.triggerValidation();
    }

    @action.bound private setContractTermStart(date: Date | null | undefined): void {
        if (!this.vehicle) {
            throw new Error("Can't update contractTermStart: Vehicle not loaded.");
        }
        this.vehicle.contractTermStart = date ?? undefined;
        this.triggerValidation();
    }

    @action.bound private setContractTermEnd(date: Date | null | undefined): void {
        if (!this.vehicle) {
            throw new Error("Can't update contractTermEnd: Vehicle not loaded.");
        }
        this.vehicle.contractTermEnd = date ?? undefined;
        this.triggerValidation();
    }

    @action.bound private setEngineType(
        _event: FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
        _index?: number,
    ): void {
        if (!this.vehicle) {
            throw new Error("Can't update engineType: Vehicle not loaded.");
        }
        if (!option) {
            this.vehicle.engineType = EngineType.ELECTRIC;
            this.triggerValidation();
            return;
        }

        this.vehicle.engineType =
            EngineType[String(option.key).toUpperCase() as keyof typeof EngineType] ||
            EngineType.ELECTRIC;
        this.triggerValidation();
    }

    @action.bound private closeCancelConfirmationModalVisible(): void {
        this.cancelConfirmationModalVisible = false;
    }

    public get yearOptions(): IDropdownOption[] {
        const options: IDropdownOption[] = [];
        const currentYear = new Date().getFullYear();
        for (let i = currentYear; i > 1924; i--) {
            options.push({ key: i, text: i.toString() });
        }
        return options;
    }

    public onFormatDate = (date?: Date): string => {
        return store.i18n.formatDateOnly(date!);
    };

    public formatHoursUntilNextMaintenance(hour: number | undefined): string {
        if (hour === undefined) {
            return "";
        }

        return `${store.i18n.formatHours(Math.abs(hour))} ${
            hour <= 0 ? store.i18n.t("formUpdateVehicle.overdue") : ""
        }`;
    }

    public renderGeneralSection(): JSX.Element {
        const engineTypeOptions: IDropdownOption[] = [
            {
                key: "electric",
                text: store.i18n.t("formCreateVehicle.engineTypeOptions.electric"),
            },
            {
                key: "combustion",
                text: store.i18n.t("formCreateVehicle.engineTypeOptions.combustion"),
            },
        ];
        return (
            <Stack
                horizontal
                horizontalAlign="space-between"
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Stack>
                    <Toggle
                        label={store.i18n.t("formUpdateVehicle.active.label")}
                        checked={this.vehicle?.status != VehicleStatus.INACTIVE}
                        onChange={this.setActive}
                    />
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.id.label")}
                        {...doubleBindString(this.vehicle!, "serialNumber", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "serialNumber",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.manufacturer.label")}
                        {...doubleBindNullableStringEmptyAsNull(this.vehicle!, "manufacturer", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "manufacturer",
                            ),
                        )}
                    />
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.model.label")}
                        {...doubleBindNullableStringEmptyAsNull(this.vehicle!, "model", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "model",
                            ),
                        )}
                    />
                    <SearchableDropdown
                        formatEntity={(vehicleType: VehicleType) =>
                            store.i18n.formatVehicleType(vehicleType)
                        }
                        repository={store.repositoryVehicleTypes}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formUpdateUser.vehicleType.label")}
                        onChange={this.setVehicleType}
                        selectedKey={this.vehicle?.vehicleTypeId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "vehicleTypeId",
                            ),
                        )}
                        hideSearch
                        clearable
                    />
                    <Dropdown
                        label={store.i18n.t("formUpdateVehicle.yearOfBuild.label")}
                        selectedKey={this.vehicle?.manufactureYear}
                        onChange={this.setManufactureYear}
                        options={this.yearOptions}
                        calloutProps={{ directionalHintFixed: true }}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "manufactureYear",
                            ),
                        )}
                    />
                    <SearchableDropdown
                        formatEntity={(department: Department) =>
                            store.i18n.formatDepartment(department)
                        }
                        repository={store.repositoryDepartments}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formUpdateUser.department.label")}
                        onChange={this.setDepartment}
                        selectedKey={this.vehicle?.departmentId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "departmentId",
                            ),
                        )}
                        clearable
                    />
                    <SearchableDropdown
                        formatEntity={store.i18n.formatPreOpsChecklist}
                        repository={store.repositoryPreOpsChecklists}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formUpdateVehicle.preOpsChecklist.label")}
                        onChange={this.setPreOpsChecklist}
                        selectedKey={this.vehicle?.preOpsChecklistId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "preOpsChecklistId",
                            ),
                        )}
                        clearable
                    />
                </Stack>
                <Stack>
                    <Toggle
                        label={store.i18n.t("formUpdateVehicle.checklistOverride.label")}
                        checked={this.vehicle?.checklistOverride}
                        onChange={this.setChecklistOverride}
                    />
                    <SearchableDropdown
                        formatEntity={(shockProfile: ShockProfile) => shockProfile.label}
                        repository={store.repositoryShockProfiles}
                        query={{ pageSize: defaultPageSize }}
                        label={store.i18n.t("formUpdateVehicle.shockProfile.label")}
                        onChange={this.setShockProfile}
                        selectedKey={this.vehicle?.shockProfileId}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "shockProfileId",
                            ),
                        )}
                        hideSearch
                        clearable
                    />
                    <TextField
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.idleAutoLogoutAfter.label")}
                        {...doubleBindIntegerToString(this.vehicle!, "idleAutoLogoutAfter", () =>
                            this.triggerValidation(),
                        )}
                        onChange={this.setIdleAutoLogoutAfter}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "idleAutoLogoutAfter",
                            ),
                        )}
                    />

                    <Dropdown
                        label={store.i18n.t("formUpdateVehicle.engineTypeOptions.label")}
                        options={engineTypeOptions}
                        selectedKey={this.vehicle === undefined ? null : this.vehicle.engineType}
                        onChange={this.setEngineType}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "engineType",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.vehicle?.engineType === EngineType.COMBUSTION
                        }
                        label={store.i18n.t("formUpdateVehicle.magneticFieldThreshold.label")}
                        type="number"
                        {...doubleBindIntegerToString(this.vehicle!, "magneticFieldThreshold", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "magneticFieldThreshold",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.vehicle?.engineType === EngineType.ELECTRIC
                        }
                        label={store.i18n.t("formUpdateVehicle.motionStartThresholdMv.label")}
                        type="number"
                        {...doubleBindIntegerToString(this.vehicle!, "motionStartThresholdMv", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "motionStartThresholdMv",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.vehicle?.engineType === EngineType.ELECTRIC
                        }
                        label={store.i18n.t("formUpdateVehicle.motionStopThresholdMv.label")}
                        type="number"
                        {...doubleBindIntegerToString(this.vehicle!, "motionStopThresholdMv", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "motionStopThresholdMv",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.vehicle?.engineType === EngineType.ELECTRIC
                        }
                        label={store.i18n.t("formUpdateVehicle.motionStartDelayMs.label")}
                        type="number"
                        {...doubleBindIntegerToString(this.vehicle!, "motionStartDelayMs", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "motionStartDelayMs",
                            ),
                        )}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole !=
                                ManagementRole.SUPER_ADMIN ||
                            this.vehicle?.engineType === EngineType.ELECTRIC
                        }
                        label={store.i18n.t("formUpdateVehicle.motionStopDelayMs.label")}
                        type="number"
                        {...doubleBindIntegerToString(this.vehicle!, "motionStopDelayMs", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "motionStopDelayMs",
                            ),
                        )}
                    />
                </Stack>
            </Stack>
        );
    }

    public renderFinancingSection(): JSX.Element {
        const procurementOptions: IDropdownOption[] = [
            {
                key: "purchase",
                text: store.i18n.t("formUpdateVehicle.procurementOptions.purchase"),
            },
            {
                key: "leasing",
                text: store.i18n.t("formUpdateVehicle.procurementOptions.leasing"),
            },
            {
                key: "rental",
                text: store.i18n.t("formUpdateVehicle.procurementOptions.rental"),
            },
        ];

        return (
            <Stack
                horizontal
                horizontalAlign="space-between"
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Stack>
                    <ClearableDropdown
                        label={store.i18n.t("formUpdateVehicle.procurementOptions.label")}
                        onChange={this.setProcurementType}
                        options={procurementOptions}
                        selectedKey={this.vehicle?.procurementType}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "procurementType",
                            ),
                        )}
                    />
                    <DatePicker
                        disabled={this.vehicle?.procurementType !== ProcurementType.PURCHASE}
                        isRequired={this.vehicle?.procurementType === ProcurementType.PURCHASE}
                        formatDate={this.onFormatDate}
                        placeholder={store.i18n.t("formCreateVehicle.datePicker.placeholder")}
                        label={store.i18n.t("formCreateVehicle.purchaseDate.label")}
                        value={this.vehicle?.purchaseDate || undefined}
                        onSelectDate={this.setPurchaseDate}
                    />
                    <DatePicker
                        disabled={
                            this.vehicle?.procurementType !== ProcurementType.LEASING &&
                            this.vehicle?.procurementType !== ProcurementType.RENTAL
                        }
                        isRequired={
                            this.vehicle?.procurementType === ProcurementType.LEASING ||
                            this.vehicle?.procurementType === ProcurementType.RENTAL
                        }
                        formatDate={this.onFormatDate}
                        maxDate={
                            this.vehicle && this.vehicle.contractTermEnd
                                ? this.vehicle.contractTermEnd
                                : undefined
                        }
                        placeholder={store.i18n.t("formUpdateVehicle.datePicker.placeholder")}
                        label={store.i18n.t("formUpdateVehicle.contractTermStart.label")}
                        value={this.vehicle?.contractTermStart || undefined}
                        onSelectDate={this.setContractTermStart}
                        strings={store.i18n.datePickerStrings(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "contractTermStart",
                            ),
                        )}
                    />
                </Stack>
                <Stack>
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.purchasePrice.label")}
                        styles={{ root: { visibility: "hidden" } }}
                    />
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.purchasePrice.label")}
                        styles={{ root: { visibility: "hidden" } }}
                    />
                    <DatePicker
                        disabled={
                            this.vehicle?.procurementType !== ProcurementType.LEASING &&
                            this.vehicle?.procurementType !== ProcurementType.RENTAL
                        }
                        isRequired={
                            this.vehicle?.procurementType === ProcurementType.LEASING ||
                            this.vehicle?.procurementType === ProcurementType.RENTAL
                        }
                        minDate={
                            this.vehicle && this.vehicle.contractTermStart
                                ? this.vehicle.contractTermStart
                                : undefined
                        }
                        value={this.vehicle?.contractTermEnd || undefined}
                        formatDate={this.onFormatDate}
                        placeholder={store.i18n.t("formUpdateVehicle.datePicker.placeholder")}
                        label={store.i18n.t("formUpdateVehicle.contractTermEnd.label")}
                        onSelectDate={this.setContractTermEnd}
                    />
                </Stack>
                <Stack>
                    <TooltipHost content={store.i18n.t("generic.comingSoon")}>
                        <TextField
                            disabled
                            label={store.i18n.t("formUpdateVehicle.contractFile.label")}
                        />
                    </TooltipHost>
                    <TextField
                        disabled={this.vehicle?.procurementType !== ProcurementType.PURCHASE}
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.purchasePrice.label")}
                        suffix="$"
                        {...(this.vehicle?.procurementType === ProcurementType.PURCHASE &&
                            doubleBindIntegerToString(this.vehicle, "purchaseCost", () =>
                                this.triggerValidation(),
                            ))}
                        errorMessage={
                            this.vehicle?.procurementType === ProcurementType.PURCHASE
                                ? store.i18n.formatFieldValidationState(
                                      store.repositoryVehicles.validation.update.getFieldValidationState(
                                          this.validationId,
                                          "purchaseCost",
                                      ),
                                  )
                                : undefined
                        }
                    />
                    <TextField
                        disabled={
                            this.vehicle?.procurementType !== ProcurementType.LEASING &&
                            this.vehicle?.procurementType !== ProcurementType.RENTAL
                        }
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.monthlyCost.label")}
                        suffix="$"
                        value={this.vehicle?.monthlyCost?.toString()}
                        {...((this.vehicle?.procurementType === ProcurementType.LEASING ||
                            this.vehicle?.procurementType === ProcurementType.RENTAL) &&
                            doubleBindIntegerToString(this.vehicle, "monthlyCost", () =>
                                this.triggerValidation(),
                            ))}
                        errorMessage={
                            this.vehicle?.procurementType === ProcurementType.LEASING
                                ? store.i18n.formatFieldValidationState(
                                      store.repositoryVehicles.validation.update.getFieldValidationState(
                                          this.validationId,
                                          "monthlyCost",
                                      ),
                                  )
                                : undefined
                        }
                    />
                </Stack>
            </Stack>
        );
    }

    public renderMaintenanceSection(): JSX.Element {
        return (
            <Stack
                horizontal
                horizontalAlign="space-between"
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Stack>
                    <TextField
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.initialOperatingHours.label")}
                        {...doubleBindIntegerToString(this.vehicle!, "initialOperatingHours", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "initialOperatingHours",
                            ),
                        )}
                    />
                    <DatePicker
                        disabled
                        formatDate={this.onFormatDate}
                        placeholder={store.i18n.t("formUpdateVehicle.datePicker.placeholder")}
                        label={store.i18n.t("formUpdateVehicle.lastMaintenance.label")}
                        value={this.vehicle?.lastMaintenanceDate || undefined}
                    />
                    <TextField
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.maintenanceNotificationAt.label")}
                        {...doubleBindIntegerToString(
                            this.vehicle!,
                            "maintenanceNotificationAt",
                            () => this.triggerValidation(),
                        )}
                        onChange={this.setMaintenanceNotificationAt}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "maintenanceNotificationAt",
                            ),
                        )}
                    />
                </Stack>
                <Stack>
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.operatingHours.label")}
                        readOnly
                        value={
                            this.vehicle
                                ? store.i18n.formatFloat(
                                      this.vehicle.totalOperatingSeconds / 3600,
                                      2,
                                  )
                                : ""
                        }
                    />
                    <TextField
                        label={store.i18n.t("formUpdateVehicle.hoursSinceLastMaintenance.label")}
                        readOnly
                        value={this.vehicle?.lastMaintenanceOperatingHours?.toString() || undefined}
                    />
                    <Toggle
                        label={store.i18n.t("formUpdateVehicle.maintenanceLockdown.label")}
                        checked={this.vehicle?.maintenanceLockdown}
                        onChange={this.setMaintenanceLockdown}
                    />
                </Stack>
                <Stack>
                    <TextField
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.maintenanceInterval.label")}
                        {...doubleBindIntegerToString(this.vehicle!, "maintenanceInterval", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "maintenanceInterval",
                            ),
                        )}
                    />
                    <TextField
                        disabled
                        label={store.i18n.t("formUpdateVehicle.hoursUntilMaintenance.label")}
                        readOnly
                        value={this.formatHoursUntilNextMaintenance(
                            this.vehicle?.hoursUntilNextMaintenance,
                        )}
                    />
                    <TextField
                        type="number"
                        label={store.i18n.t("formUpdateVehicle.weeklyDriveTimeGoal.label")}
                        {...doubleBindIntegerToString(this.vehicle!, "weeklyDriveGoalHours", () =>
                            this.triggerValidation(),
                        )}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "weeklyDriveGoalHours",
                            ),
                        )}
                    />
                </Stack>
            </Stack>
        );
    }

    public renderMiscellaneousSection(): JSX.Element {
        return (
            <Stack
                horizontal
                horizontalAlign="space-between"
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Stack>
                    <TextField
                        disabled
                        label={store.i18n.t("formUpdateVehicle.currentStatus.label")}
                        value={mapSome(
                            (status) => store.i18n.t(`formUpdateVehicle.statusOptions.${status}`),
                            this.vehicle?.status,
                        )}
                    />
                    <TextField
                        disabled
                        label={store.i18n.t("formUpdateVehicle.lastLocation.label")}
                    />
                </Stack>
                <Stack>
                    <TextField
                        disabled
                        label={store.i18n.t("formUpdateVehicle.lastDriver.label")}
                        readOnly
                        value={this.vehicle && store.i18n.formatUserFullName(this.lastDriver)}
                    />
                    <TextField
                        disabled={
                            store.serviceOwnUser.user?.managementRole != ManagementRole.SUPER_ADMIN
                        }
                        label={store.i18n.t("formUpdateVehicle.phoneImei.label")}
                        {...doubleBindNullableString(this.vehicle!, "phoneImei", () =>
                            this.triggerValidation(),
                        )}
                        onChange={this.setPhoneImei}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositoryVehicles.validation.update.getFieldValidationState(
                                this.validationId,
                                "phoneImei",
                            ),
                        )}
                    />
                </Stack>
                <Stack>
                    <TextField
                        disabled
                        label={store.i18n.t("formUpdateVehicle.lastSynchronization.label")}
                        readOnly
                        value={
                            (this.vehicle?.lastSynchronizedAt &&
                                store.i18n.formatDateTime(this.vehicle.lastSynchronizedAt)) ??
                            ""
                        }
                    />
                    <SearchableDropdown
                        disabled={
                            store.serviceOwnUser.user?.managementRole != ManagementRole.SUPER_ADMIN
                        }
                        label={store.i18n.t("formUpdateVehicle.vehicleDevice.label")}
                        formatEntity={(vehicleDevice: VehicleDevice) => vehicleDevice.serialNumber}
                        repository={store.repositoryVehicleDevices}
                        query={{
                            pageSize: defaultPageSize,
                            status: VehicleDeviceStatus.NOT_ASSIGNED,
                        }}
                        onChange={this.setVehicleDevice}
                        selectedKey={this.newVehicleDeviceId}
                        clearable
                    />
                </Stack>
            </Stack>
        );
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateVehicle.submit.text")}
                validation={store.repositoryVehicles.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateVehicle}>
                <Stack
                    horizontal
                    horizontalAlign="space-between"
                    tokens={{
                        childrenGap: "1em",
                        padding: `0px ${sizes.formPaddingHorizontal}`,
                    }}
                >
                    <Stack>
                        <h2>{store.i18n.t("formUpdateVehicle.title.general")}</h2>
                        {this.renderGeneralSection()}
                        <Separator />
                        <h2>{store.i18n.t("formUpdateVehicle.title.financing")}</h2>
                        {this.renderFinancingSection()}
                    </Stack>
                    <Separator vertical />
                    <Stack>
                        <h2>{store.i18n.t("formUpdateVehicle.title.maintenance")}</h2>
                        {this.renderMaintenanceSection()}
                        <Separator />
                        <h2>{store.i18n.t("formUpdateVehicle.title.miscellaneous")}</h2>
                        {this.renderMiscellaneousSection()}
                    </Stack>
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateVehicle.cancel.text")}
                            onClick={this.showConfirmAndCallDialogCancelCallback}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
                <ModalConfirmation
                    isOpen={this.cancelConfirmationModalVisible}
                    title={store.i18n.t("modalAbortUpdate.title")}
                    text={store.i18n.t("modalAbortUpdate.description")}
                    onConfirm={this.props.onDialogCancel}
                    onCancel={this.closeCancelConfirmationModalVisible}
                />
            </form>
        );
    }
}
